import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout, { Eyebrow, PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { DEFAULT_PAGES, useSiteContent } from "@/lib/siteContent";

function destOf(o) {
  return o.square_payment_url || o.internal_route || `${ROUTES.wellnessAssessment}?context=program`;
}
function isExternal(o) {
  return !!o.square_payment_url;
}

export default function Programs() {
  const hdr = useSiteContent("pages", DEFAULT_PAGES).programs || DEFAULT_PAGES.programs;
  const [programs, setPrograms] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clinic-offers?type=program");
        setPrograms(data.offers || []);
      } catch {
        setPrograms([]);
      }
    })();
  }, []);

  return (
    <PublicLayout>
      <section style={{ background: C.lightBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow>{hdr.eyebrow}</Eyebrow>
          <h1 className="text-4xl sm:text-5xl leading-[1.05] text-slate-900 max-w-3xl" style={SERIF} data-testid="programs-h1">{hdr.heading}</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">{hdr.subcopy}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {programs === null ? (
          <p className="text-sm text-slate-500">Loading programs…</p>
        ) : programs.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ border: `1px dashed ${C.line}`, background: C.lightBg }} data-testid="programs-empty">
            <p className="text-slate-700" style={SERIF}>New programs are coming soon.</p>
            <p className="mt-2 text-sm text-slate-600">In the meantime, a wellness assessment is the best place to begin.</p>
            <div className="mt-6 flex justify-center"><PrimaryBtn to={ROUTES.wellnessAssessment}>Start My Assessment <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-start" data-testid="programs-grid">
            {programs.map((p) => (
              <div key={p.id || p.title} className="rounded-2xl p-7 bg-white" style={{ border: `1px solid ${C.line}` }} data-testid={`program-${p.id}`}>
                <div className="text-xl font-semibold text-slate-900" style={SERIF}>{p.title}</div>
                <p className="mt-3 text-sm text-slate-600">{p.short_description}</p>
                {p.price_display ? <p className="mt-3 text-sm font-medium text-slate-800">{p.price_display}</p> : null}
                <div className="mt-6">
                  {isExternal(p)
                    ? <PrimaryBtn href={destOf(p)}>{p.cta_label || "Enroll"} <ArrowRight className="h-4 w-4" /></PrimaryBtn>
                    : <GhostBtn to={destOf(p)}>{p.cta_label || "Learn More"}</GhostBtn>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-10 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ background: C.lightBg }}>
          <p className="text-slate-800" style={SERIF}>Not sure which program fits?</p>
          <GhostBtn to={ROUTES.startHere}>Help Me Choose</GhostBtn>
        </div>
      </section>
    </PublicLayout>
  );
}
